import { FormattedMessage, useIntl } from 'react-intl';
import { formatPhoneNumberIntl } from 'react-phone-number-input';

import Phone from '@mui/icons-material/Phone';
import QuestionAnswer from '@mui/icons-material/QuestionAnswer';
import { alpha, IconButton, Typography, useMediaQuery } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import { Constants, MEDIA_QUERIES } from '@arrived/common';

import { useMediaQueryClasses } from '../hooks';

const StyledWrapper = styled.div`
  &.arrived-phone-number {
    display: flex;
    align-items: center;
    margin-left: ${({ theme }) => theme.spacing(4)};

    &:not(.mobile) {
      border: solid ${({ theme }) => theme.palette.neutrals.platinum};
      border-width: 0 1px;
      padding: ${({ theme }) => theme.spacing(0, 3.5, 0, 0.5)};
      margin: ${({ theme }) => theme.spacing(0.5, 0.5, 0.5, 0)};
      transition: border-color 0.3s ease-in-out;
    }

    .icon {
      .MuiIconButton-label {
        color: ${({ theme }) => theme.palette.tertiary[500]};
      }

      .MuiIconButton-root {
        background-color: rgba(247, 247, 247);

        &:hover {
          background-color: rgba(215, 215, 215, 0.5);
        }
      }
    }

    .contents {
      .middle-text {
        color: ${({ theme }) => theme.palette.primary.main};
      }
    }

    .left-icon,
    .right-icon {
      color: ${({ theme }) => alpha(theme.palette.darkened.midnight, 0.4)};
    }

    .left-icon,
    .right-icon,
    .contents {
      margin-left: ${({ theme }) => theme.spacing(1)};
    }

    .contents {
      .top-text,
      .bottom-text {
        font-size: 13px;
        line-height: normal;
      }

      .middle-text {
        line-height: 1em;
      }
    }

    &.top-nav-truncate {
      padding-right: ${({ theme }) => theme.spacing(0.5)};

      .left-icon {
        margin-left: 0;
      }

      .contents {
        display: none;
      }
    }

    &.mobile {
      width: auto;
      padding: ${({ theme }) => theme.spacing(2, 0)};
      border-top: 1px solid ${({ theme }) => theme.palette.neutrals.platinum};
      margin: ${({ theme }) => theme.spacing(3, 2, 0)};

      .left-icon {
        margin-left: 0;
      }

      .contents {
        .middle-text {
          color: ${({ theme }) => theme.palette.primary.main};
        }
      }
    }
  }
`;

export const ArrivedPhoneNumber = () => {
  const intl = useIntl();
  const isTopNavTruncate = useMediaQuery(MEDIA_QUERIES.isTopNavTruncate);
  const mediaQueryClasses = useMediaQueryClasses();

  const formattedPhoneNumber = formatPhoneNumberIntl(`+${String(Constants.arrivedPhoneNumber)}`);

  return (
    <StyledWrapper
      className={clsx('arrived-phone-number', mediaQueryClasses, {
        'top-nav-truncate': isTopNavTruncate,
      })}
    >
      <div className="left-icon">
        <IconButton
          title={intl.formatMessage({
            id: 'arrived-phone-number-description',
            defaultMessage: "Questions? Let's chat",
          })}
          className="open-intercom"
          size="large"
        >
          <QuestionAnswer />
        </IconButton>
      </div>
      <div className="right-icon">
        <IconButton
          href={`tel:${formattedPhoneNumber}`}
          title={intl.formatMessage({
            id: 'arrived-phone-number-description',
            defaultMessage: 'Questions? Call us',
          })}
          size="large"
        >
          <Phone />
        </IconButton>
      </div>
      <div className="contents">
        <Typography className="top-text" noWrap variant="caption">
          <FormattedMessage id="arrived-phone-number-description" defaultMessage="Questions?" />
        </Typography>
        <a href={`tel:${formattedPhoneNumber}`} className="bottom-text-link">
          <Typography className="middle-text" noWrap variant="h5">
            {Constants.arrivedNamedPhoneNumber}
          </Typography>
        </a>
        <a href={`tel:${formattedPhoneNumber}`} className="bottom-text-link">
          <Typography noWrap variant="caption" className="bottom-text">
            {formattedPhoneNumber}
          </Typography>
        </a>
      </div>
    </StyledWrapper>
  );
};
